/**
 * Sidebar Navigation Component
 * Main navigation menu for the GZOO AI Marketing platform
 */
'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Building2, ShieldCheck } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { GlobalSearchBar } from '@/components/search/GlobalSearchBar';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useBranding } from '@/hooks/useBranding';
import { useAgencyStatus } from '@/hooks/useAgency';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
  name: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  children?: { name: string; href: string }[];
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: 'Overview',
    items: [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        ),
      },
      {
        name: 'Orchestrator',
        href: '/orchestrator',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Marketing',
    items: [
      {
        name: 'Campaigns',
        href: '/campaigns',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
          </svg>
        ),
        children: [
          { name: 'All Campaigns', href: '/campaigns' },
          { name: 'Create Campaign', href: '/campaigns/new' },
        ],
      },
      {
        name: 'Paid Ads',
        href: '/test-ads',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        ),
        children: [
          { name: 'Test Ads', href: '/test-ads' },
          { name: 'Ad Boosts', href: '/ad-boosts' },
          { name: 'Conversions', href: '/conversions' },
          { name: 'Connections', href: '/connections' },
        ],
      },
      {
        name: 'Inbox',
        href: '/inbox',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l2-2h14l2 2m-18 0v8a2 2 0 002 2h12a2 2 0 002-2V8m-18 0h5l2 3h4l2-3h5" />
          </svg>
        ),
      },
      {
        name: 'Workflows',
        href: '/workflows',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        ),
        children: [
          { name: 'All Workflows', href: '/workflows' },
          { name: 'Create Workflow', href: '/workflows/new' },
        ],
      },
      {
        name: 'Landing Pages',
        href: '/landing-pages',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        children: [
          { name: 'All Pages', href: '/landing-pages' },
          { name: 'Create Page', href: '/landing-pages/new' },
        ],
      },
      {
        name: 'Outreach Emails',
        href: '/emails',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        ),
        children: [
          { name: 'All Emails', href: '/emails' },
          { name: 'Create Email', href: '/emails/new' },
        ],
      },
      {
        name: 'Newsletters',
        href: '/newsletters',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m0 0l4 2v9a2 2 0 01-2 2h-2m-2-13v13M9 8h4m-4 4h4" />
          </svg>
        ),
        children: [
          { name: 'All Newsletters', href: '/newsletters' },
          { name: 'Create Newsletter', href: '/newsletters/new' },
        ],
      },
      {
        name: 'Drip Campaigns',
        href: '/drips',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3s5 5.5 5 10a5 5 0 01-10 0c0-4.5 5-10 5-10z" />
          </svg>
        ),
        children: [
          { name: 'All Drips', href: '/drips' },
          { name: 'Create Drip', href: '/drips/new' },
        ],
      },
      {
        name: 'Content Sources',
        href: '/sources',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a14 14 0 0114 14M5 11a8 8 0 018 8M6 18a1 1 0 100-2 1 1 0 000 2z" />
          </svg>
        ),
      },
      {
        name: 'Email Templates',
        href: '/email-templates',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v3H4V5z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h7v12H5a1 1 0 01-1-1V8zM13 8h7v11a1 1 0 01-1 1h-6V8z" />
          </svg>
        ),
        children: [
          { name: 'All Templates', href: '/email-templates' },
          { name: 'New Template', href: '/email-templates/new' },
        ],
      },
      {
        name: 'Forms',
        href: '/forms',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3v4a2 2 0 002 2h4" />
          </svg>
        ),
      },
      {
        name: 'Form Routing',
        href: '/forms/routing',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
      },
      {
        name: 'Booking',
        href: '/booking',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
        children: [
          { name: 'Meeting Types', href: '/booking' },
          { name: 'New Meeting Type', href: '/booking/meeting-types/new/edit' },
        ],
      },
    ],
  },
  {
    title: 'Audience',
    items: [
      {
        name: 'Contacts',
        href: '/contacts',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ),
        children: [
          { name: 'All Contacts', href: '/contacts' },
          { name: 'Hot Leads', href: '/contacts?tab=hot_leads' },
          { name: 'Lists', href: '/contacts?tab=lists' },
          { name: 'Lead Scoring', href: '/contacts?tab=scoring' },
        ],
      },
    ],
  },
  {
    title: 'Analytics',
    items: [
      {
        name: 'Overview',
        href: '/analytics',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        children: [
          { name: 'Performance', href: '/analytics' },
          { name: 'Attribution', href: '/analytics/attribution' },
          { name: 'Funnels', href: '/analytics/funnels' },
          { name: 'ROI', href: '/analytics/roi' },
        ],
      },
      {
        name: 'Reports',
        href: '/analytics/reports',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        name: 'Alerts',
        href: '/analytics/alerts',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        ),
      },
      {
        name: 'Predictions',
        href: '/predictions',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Content',
    items: [
      {
        name: 'Video Studio',
        href: '/video-studio',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        ),
        children: [
          { name: 'Projects', href: '/video-studio' },
          { name: 'Media Gallery', href: '/video-studio/gallery' },
          { name: 'Settings', href: '/video-studio/settings' },
        ],
      },
      {
        name: 'Activity',
        href: '/activity',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Knowledge Vault',
    items: [
      {
        name: 'Vault Overview',
        href: '/vault',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
        ),
        children: [
          { name: 'Overview', href: '/vault' },
          { name: 'Products', href: '/vault/products' },
          { name: 'Channels', href: '/vault/channels' },
          { name: 'Strategy', href: '/vault/strategy' },
          { name: 'Research Briefs', href: '/vault/research' },
          { name: 'Personas', href: '/personas' },
        ],
      },
    ],
  },
  {
    title: 'Integrations',
    items: [
      {
        name: 'All Integrations',
        href: '/integrations',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        ),
        children: [
          { name: 'All Integrations', href: '/integrations' },
          { name: 'SendGrid', href: '/integrations/sendgrid' },
          { name: 'Resend', href: '/integrations/resend' },
          { name: 'Mailchimp', href: '/integrations/mailchimp' },
          { name: 'HubSpot', href: '/integrations/hubspot' },
          { name: 'Salesforce', href: '/integrations/salesforce' },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    items: [
      {
        name: 'Profile',
        href: '/settings/profile',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      },
      {
        name: 'Brand',
        href: '/settings/brand',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        ),
        children: [
          { name: 'Brand Kit', href: '/settings/brand' },
          { name: 'Brand Voice', href: '/settings/brand-voice' },
          { name: 'Branding (white-label)', href: '/settings/branding' },
        ],
      },
      {
        name: 'Billing',
        href: '/settings/billing',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        ),
      },
      {
        name: 'Usage',
        href: '/settings/usage',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
      {
        name: 'Audit Logs',
        href: '/settings/audit-logs',
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
    ],
  },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const branding = useBranding();
  const { data: agencyStatus } = useAgencyStatus();
  const isAgency = !!agencyStatus?.is_agency;
  const { user } = useAuth();
  const isOperator = !!user?.is_operator;
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const toggleExpand = (itemName: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemName)) {
        next.delete(itemName);
      } else {
        next.add(itemName);
      }
      return next;
    });
  };

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      return pathname === '/dashboard' || pathname === '/';
    }
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-40 h-screen bg-white dark:bg-brand-dark-secondary/95 backdrop-blur-xl border-r border-gray-200 dark:border-white/10 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
    >
      {/* Logo */}
      <div className={`h-20 flex items-center border-b border-gray-200 dark:border-white/5 ${
        collapsed ? 'flex-col justify-center py-2 gap-1' : 'justify-between px-6'
      }`}>
        {!collapsed && (
          <Link href="/dashboard" className="flex items-center justify-center w-full">
            <img
              src={branding.logo_url}
              alt={branding.app_name}
              className="h-9 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]"
            />
          </Link>
        )}
        {collapsed && (
          <Link href="/dashboard">
            <img
              src={branding.logo_icon_url}
              alt={branding.app_name}
              className="h-8 w-8 drop-shadow-[0_0_15px_rgba(255,107,0,0.3)]"
            />
          </Link>
        )}
      </div>

      {/* Collapse Toggle Button - positioned outside */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 bg-white dark:bg-brand-dark-secondary border border-gray-200 dark:border-white/20 text-gray-600 dark:text-white p-1 rounded-full shadow-lg hover:scale-110 transition-transform z-50"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        )}
      </button>

      {/* Navigation */}
      <nav className="h-[calc(100vh-5rem)] overflow-y-auto py-6 px-3 space-y-2"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.1) transparent',
        }}
      >
        {/* Platform admin — only for the SaaS owner (operator). Sits above all tenants. */}
        {isOperator && (
          <div className="mb-6">
            {!collapsed && (
              <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Platform
              </h3>
            )}
            <ul className="space-y-1">
              <li className="relative">
                <Link
                  href="/admin/tenants"
                  title="Tenants"
                  className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-xl transition-all duration-200 ${
                    isActive('/admin/tenants')
                      ? 'bg-gradient-to-r from-brand-orange/20 to-transparent text-gray-900 dark:text-white border border-brand-orange/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <ShieldCheck className={`h-5 w-5 shrink-0 ${isActive('/admin/tenants') ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="text-sm font-medium">Tenants</span>}
                </Link>
              </li>
            </ul>
          </div>
        )}
        {/* Agency layer (Wave 6 Phase 2) — only for agency orgs */}
        {isAgency && (
          <div className="mb-6">
            {!collapsed && (
              <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Agency
              </h3>
            )}
            <ul className="space-y-1">
              <li className="relative">
                <Link
                  href="/clients"
                  title="Clients"
                  className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-xl transition-all duration-200 ${
                    isActive('/clients')
                      ? 'bg-gradient-to-r from-brand-orange/20 to-transparent text-gray-900 dark:text-white border border-brand-orange/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Building2 className={`h-5 w-5 shrink-0 ${isActive('/clients') ? 'text-brand-orange' : ''}`} />
                  {!collapsed && <span className="text-sm font-medium">Clients</span>}
                </Link>
              </li>
            </ul>
          </div>
        )}
        {navigation.map((section) => (
          <div key={section.title} className="mb-6">
            {!collapsed && (
              <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => {
                const active = isActive(item.href);
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.name);

                return (
                  <li key={item.name} className="relative">
                    {hasChildren ? (
                      <>
                        {/* When collapsed: show as link + flyout on hover */}
                        {collapsed ? (
                          <div
                            className="relative"
                            onMouseEnter={() => setHoveredItem(item.name)}
                            onMouseLeave={() => setHoveredItem(null)}
                          >
                            <Link
                              href={item.href}
                              className={`flex items-center justify-center px-3 py-2 rounded-xl transition-all duration-200 ${
                                active
                                  ? 'bg-gradient-to-r from-brand-orange/20 to-transparent text-gray-900 dark:text-white border border-brand-orange/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                              title={item.name}
                            >
                              <span className={active ? 'text-brand-orange' : 'text-gray-500 group-hover:text-brand-orange transition-colors'}>
                                {item.icon}
                              </span>
                            </Link>
                            {/* Flyout menu on hover */}
                            {hoveredItem === item.name && item.children && (
                              <div className="absolute left-full top-0 ml-2 py-2 w-48 bg-white/95 dark:bg-brand-dark-secondary/95 backdrop-blur-xl rounded-xl shadow-lg border border-gray-200 dark:border-white/10 z-50">
                                <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-white/5">
                                  {item.name}
                                </div>
                                {item.children.map((child) => (
                                  <Link
                                    key={child.href}
                                    href={child.href}
                                    className={`block px-3 py-2 text-sm transition-colors ${
                                      pathname === child.href
                                        ? 'text-brand-orange bg-brand-orange/10'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                                  >
                                    {child.name}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* When expanded: show toggle button + inline children */
                          <>
                            <button
                              onClick={() => toggleExpand(item.name)}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group relative ${
                                active
                                  ? 'bg-gradient-to-r from-brand-orange/20 to-transparent text-gray-900 dark:text-white border border-brand-orange/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                              }`}
                            >
                              <span className={`${active ? 'text-brand-orange' : 'text-gray-500 group-hover:text-brand-orange'} transition-colors`}>
                                {item.icon}
                              </span>
                              <span className="flex-1 text-sm font-medium text-left">{item.name}</span>
                              <svg
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              {/* Active Indicator Line */}
                              {active && (
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-orange rounded-r-full blur-[2px]" />
                              )}
                            </button>
                            {isExpanded && item.children && (
                              <ul className="mt-1 ml-8 space-y-1">
                                {item.children.map((child) => (
                                  <li key={child.href}>
                                    <Link
                                      href={child.href}
                                      className={`block px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        pathname === child.href
                                          ? 'text-brand-orange bg-brand-orange/10'
                                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
                                      }`}
                                    >
                                      {child.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Link
                        href={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 group relative ${
                          active
                            ? 'bg-gradient-to-r from-brand-orange/20 to-transparent text-gray-900 dark:text-white border border-brand-orange/20 shadow-[0_0_15px_rgba(255,107,0,0.1)]'
                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        title={collapsed ? item.name : undefined}
                      >
                        <span className={`${active ? 'text-brand-orange' : 'text-gray-500 group-hover:text-brand-orange'} transition-colors`}>
                          {item.icon}
                        </span>
                        {!collapsed && <span className="text-sm font-medium">{item.name}</span>}
                        {item.badge && !collapsed && (
                          <span className="ml-auto px-2 py-0.5 text-xs bg-brand-orange/20 text-brand-orange rounded-full">
                            {item.badge}
                          </span>
                        )}
                        {/* Active Indicator Line */}
                        {active && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-orange rounded-r-full blur-[2px]" />
                        )}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {/* Help link */}
        <div className={`border-t border-gray-200 dark:border-white/5 py-3 ${collapsed ? 'px-2' : 'px-4'}`}>
          <Link
            href="/help"
            className={`flex items-center gap-3 rounded-lg px-3 py-2 text-gray-500 dark:text-gray-400 hover:text-brand-orange hover:bg-brand-orange/5 transition-all ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {!collapsed && <span className="text-sm font-medium">Help Guide</span>}
          </Link>
        </div>
      </nav>
    </aside>
  );
}

export default Sidebar;
